import { useEffect, useState } from 'react';
import Bugsnag from '@bugsnag/js';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { getCookieByName } from '@99designs/browser';
import { getDesignInspirationByCategory } from '@99designs/feature-design-inspiration';
import {
    ProductLandingPage,
    getProductGroupPrices,
    getProductLandingGroups,
    productLandingGroupKeys,
} from '@99designs/feature-product-landing';
import { createSSRApolloClient } from '@99designs/graph-utils';
import { Site } from '@99designs/site-navigation/lib/Site';
import DefaultPageLayout from '../../components/common/DefaultPageLayout';
import { Head } from '../../components/common/Head/';
import { PromoBarContentType, fetchPromoBar } from '../../components/common/PromoBar';
import { getContentfulClient } from '../../lib/getContentfulClient';
import { getDefaultPageProps } from '../../lib/getDefaultPageProps';
import { getFlashSaleConfig } from '../../lib/getFlashSale';
import { HOUR_IN_SECONDS } from '../../lib/time';
import type { ProductLandingGroupKey } from '@99designs/feature-product-landing';
import type { SiteConfig } from '@99designs/site-navigation/lib/Site';

export default function ProductLanding({
    host,
    siteConfig,
    categoryKey,
    randomFeaturedDesignerNumber,
    inspirationDesigns,
    userRole,
    productGroupPrices,
    flashSaleConfig,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const productGroup = getProductLandingGroups(categoryKey as ProductLandingGroupKey);
    const [promoBar, setPromoBar] = useState<PromoBarContentType | null>(null);
    const [promotionCoupon, setPromotionCoupon] = useState<string | null>(null);

    useEffect(() => {
        fetchPromoBar(categoryKey, host).then((data) => {
            setPromoBar(data);
        });
    }, [categoryKey, host]);

    useEffect(() => {
        const fetchPromotionCoupon = async () => {
            const response = await fetch('/start/xhr/coupon');
            if (response.ok) {
                const data = await response.json();
                if (data && data.promotion) {
                    setPromotionCoupon(data.promotion.campaign);
                }
            }
        };
        fetchPromotionCoupon();
    }, []);

    const isFlashSaleActive = flashSaleConfig === promotionCoupon;

    return (
        <DefaultPageLayout role={userRole} site={site} promoBar={promoBar}>
            <Head
                title={productGroup.title}
                description={productGroup.description}
                currentDomain={site.getDomain()}
                addSEOTags
            />
            <ProductLandingPage
                site={site}
                categoryKey={categoryKey}
                randomFeaturedDesignerNumber={randomFeaturedDesignerNumber}
                inspirationDesigns={inspirationDesigns}
                productGroupPrices={productGroupPrices}
                productGroup={productGroup}
                isFlashSaleActive={isFlashSaleActive}
            />
        </DefaultPageLayout>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const urlSlug = context.query.urlSlug as ProductLandingGroupKey;
    const { req, res } = context;

    if (process.env.USE_CSRF_CHECK) {
        const csrfToken = getCookieByName('_99csrf', req.headers['cookie'] || '');
        if (!csrfToken) {
            res.statusCode = 401;
            res.end();
            return {
                props: {},
            };
        }
    }

    const newBrandPacks = ['brand-starter-pack', 'brand-launch-pack']; // Since hooks cannot be called inside context,

    if (productLandingGroupKeys.includes(urlSlug)) {
        const apolloClient = createSSRApolloClient(req);
        const contentfulClient = getContentfulClient();

        const [
            defaultPageProps,
            inspirationDesignsQueryResult,
            productGroupPrices,
            flashSaleConfig,
        ] = await Promise.all([
            getDefaultPageProps(context),
            getDesignInspirationByCategory(
                apolloClient,
                newBrandPacks.includes(urlSlug) ? 'logo-design' : urlSlug
            ),
            getProductGroupPrices(apolloClient, productLandingGroupKeys),
            getFlashSaleConfig(contentfulClient),
        ]);

        if (!inspirationDesignsQueryResult.designs.length) {
            Bugsnag.notify(
                new Error('Feature Design Inspiration - empty list of designs'),
                (event) => {
                    event.context = `urlSlug="${urlSlug}"`;
                }
            );
        }

        context.res.setHeader('Surrogate-Key', 'product-landing');
        context.res.setHeader(
            'Surrogate-Control',
            `max-age=${HOUR_IN_SECONDS}, stale-while-revalidate=${HOUR_IN_SECONDS}`
        );

        return {
            props: {
                ...defaultPageProps,
                categoryKey: urlSlug,
                randomFeaturedDesignerNumber: Math.random(),
                inspirationDesigns: inspirationDesignsQueryResult.designs,
                productGroupPrices,
                flashSaleConfig,
            },
        };
    }

    return {
        notFound: true,
    };
};
